import { Switch } from '@headlessui/react';
import cn from 'classnames';
import { forwardRef } from 'react';

import { IconCheck } from 'components/UI/Icons/Check';

type TInput = JSX.IntrinsicElements['input'];

export interface Checkbox extends Pick<
  TInput,
  'aria-label' | 'checked' | 'children' | 'className' | 'disabled' | 'name'
> {
  /** Tint the box when an ancestor `.group` (e.g. a table row) is hovered.
   * That affordance promises that clicking the hovered area affects this
   * checkbox — turn it off in rows where a click does something else (or
   * nothing), so hovering the row doesn't make the checkbox look active. */
  groupHover?: boolean;
  onChange?: (checked: boolean) => void;
  size?: 16 | 20;
  variant?: 'checkbox' | 'toggle';
}

export const Checkbox = forwardRef<HTMLButtonElement, Checkbox>(
  (
    {
      'aria-label': ariaLabel,
      checked,
      children,
      className,
      disabled,
      groupHover = true,
      onChange,
      size = 20,
      variant = 'checkbox',
    },
    ref,
  ) => (
    <div
      className={cn(
        'flex w-fit items-center',
        {
          'cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
        },
        className,
      )}
      onClick={(event) => {
        // The Switch handles clicks on itself (and keyboard input) via its
        // own onChange; the wrapper only extends the hit area to the label.
        if (
          event.target instanceof Element &&
          event.target.closest('[role="switch"]')
        ) {
          return;
        }
        if (!disabled) {
          onChange?.(!checked);
        }
      }}
      role="presentation"
    >
      <Switch
        aria-label={ariaLabel}
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={onChange}
        ref={ref}
        className={cn(
          'flex items-center border outline-none transition-colors duration-200',
          {
            'border-blue-500 bg-blue-500 hover:border-blue-600 hover:bg-blue-600':
              checked && !disabled,
            'border-gray-300 bg-gray-300':
              !checked && !disabled && variant === 'toggle',
            'border-gray-400 bg-white hover:bg-gray-100':
              !checked && !disabled && variant === 'checkbox',
            'group-hover:bg-gray-100':
              groupHover && !checked && !disabled && variant === 'checkbox',
            'cursor-not-allowed border-gray-300 bg-gray-300': disabled,
            'h-4': size === 16,
            'h-5 w-5': size === 20 && variant === 'checkbox',
            'justify-end': checked && variant === 'toggle',
            'p-[2px]': variant === 'toggle',
            'relative justify-center rounded-md': variant === 'checkbox',
            'w-10 rounded-[20px]': size === 20 && variant === 'toggle',
            'w-4': size === 16 && variant === 'checkbox',
            'w-5': size === 20 && variant === 'checkbox',
            'w-8 rounded-[16px]': size === 16 && variant === 'toggle',
          },
        )}
      >
        {variant === 'checkbox' && (
          <div
            className={cn({
              '[&>svg]:h-3 [&>svg]:w-3': size === 16,
              '[&>svg]:h-4 [&>svg]:w-4': size === 20,
              '[&>svg]:text-gray-500': disabled,
              '[&>svg]:text-white': checked && !disabled,
              hidden: !checked,
            })}
          >
            <IconCheck />
          </div>
        )}
        {variant === 'toggle' && (
          <div
            className={cn('rounded-full', {
              'bg-gray-500': disabled,
              'bg-white': !disabled,
              'h-3 w-3': size === 16,
              'h-4 w-4': size === 20,
            })}
          />
        )}
      </Switch>
      {Boolean(children) && (
        <div
          className={cn('ms-2 text-14/100%', {
            'text-gray-500': disabled,
            'text-gray-800': !disabled,
          })}
        >
          {children}
        </div>
      )}
    </div>
  ),
);
